import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafeClose } from '../../../../hooks/useSafeClose';
import SharedTable from '../../../../components/shared/Table/SharedTable';
import StatusBadge from '../../../../components/shared/StatusBadge/StatusBadge';
import { formatDate } from '../../../../utils/dateUtils';
import '../../../../components/shared/forms.css';
import './CustomerModal.css';

/**
 * Modal for creating and editing customer data.
 */
export default function CustomerModal({ isOpen, onClose, customer, mode, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        customer_number: '',
        address: '',
        contact_person: '',
        email: '',
        phone: '',
        status: 'aktiv'
    });
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('stammdaten');
    const [customerProjects, setCustomerProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    
    const navigate = useNavigate();

    // Safe-close logic
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        if (customer) {
            setFormData({ ...customer });
        } else {
            setFormData({
                name: '',
                customer_number: '',
                address: '',
                contact_person: '',
                email: '',
                phone: '',
                status: 'aktiv'
            });
        }
        setErrors({});

        // Cleanup function to ensure scroll is restored
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [customer, isOpen]);

    useEffect(() => {
        if (isOpen) {
            const restoredTab = sessionStorage.getItem('customerModalActiveTab');
            if (restoredTab) {
                setActiveTab(restoredTab);
                sessionStorage.removeItem('customerModalActiveTab');
            } else {
                setActiveTab('stammdaten');
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && activeTab === 'projects' && customer?.id) {
            setLoadingProjects(true);
            import('../../../../api/projectApi').then(({ projectApi }) => {
                projectApi.getProjects()
                    .then(data => {
                        setCustomerProjects(data.filter(p => p.customer_id === customer.id && p.status !== 'deleted'));
                    })
                    .catch(console.error)
                    .finally(() => setLoadingProjects(false));
            });
        }
    }, [isOpen, activeTab, customer?.id]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        
        if (!formData.name || !formData.name.trim()) {
            newErrors.name = 'Bitte geben Sie einen Namen oder eine Firma an.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave(formData);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };


    return (
        <div 
            className="modal-overlay" 
            ref={overlayRef}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
        >
            <div className={`modal-container ${activeTab === 'projects' ? 'is-projects-tab' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{mode === 'create' ? 'Neuen Kunden anlegen' : 'Kundendaten bearbeiten'}</h2>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                {mode === 'edit' && (
                    <div className="modal-tabs">
                        <button 
                            className={`tab-btn ${activeTab === 'stammdaten' ? 'active' : ''}`}
                            onClick={() => setActiveTab('stammdaten')}
                        >
                            Stammdaten
                        </button>
                        <button 
                            className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
                            onClick={() => setActiveTab('projects')}
                        >
                            Projekte
                        </button>
                    </div>
                )}

                {activeTab === 'stammdaten' ? (
                <>
                <form onSubmit={handleSubmit} className="modal-form" noValidate>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label className="form-label">Name / Firma *</label>
                            <input 
                                type="text" 
                                name="name"
                                value={formData.name || ''} 
                                onChange={handleChange}
                                className={`form-input ${errors.name ? 'form-field-error' : ''}`}
                                placeholder="z.B. Musterbau GmbH"
                                autoFocus
                            />
                            {errors.name && <span className="form-error-message">{errors.name}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Kundennummer</label>
                            <input 
                                type="text" 
                                name="customer_number"
                                value={formData.customer_number || ''} 
                                onChange={handleChange}
                                className="form-input"
                                placeholder="z.B. K-2024-001"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Status</label>
                            <select 
                                name="status"
                                value={formData.status || 'aktiv'} 
                                onChange={handleChange}
                                className="form-input"
                            >
                                <option value="aktiv">Aktiv</option>
                                <option value="inaktiv">Inaktiv</option>
                            </select>
                        </div>

                        <div className="form-group full-width">
                            <label className="form-label">Adresse</label>
                            <textarea 
                                name="address"
                                value={formData.address || ''} 
                                onChange={handleChange}
                                className="form-input"
                                placeholder="Straße, PLZ, Stadt"
                                rows="3"
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Hauptkontakt</label>
                            <input 
                                type="text" 
                                name="contact_person"
                                value={formData.contact_person || ''} 
                                onChange={handleChange}
                                className="form-input"
                                placeholder="Vorname Nachname"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">E-Mail</label>
                            <input 
                                type="email" 
                                name="email"
                                value={formData.email || ''} 
                                onChange={handleChange}
                                className="form-input"
                                placeholder="kontakt@beispiel.de"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Telefon</label>
                            <input 
                                type="text" 
                                name="phone"
                                value={formData.phone || ''} 
                                onChange={handleChange}
                                className="form-input"
                                placeholder="+49 123 456789"
                            />
                        </div>
                    </div>
                </form>

                <div className="modal-footer">
                    <div className="footer-legend">* Pflichtfelder</div>
                    <div className="footer-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
                        <button type="button" className="btn-primary" onClick={handleSubmit}>
                            {mode === 'create' ? 'Kunde anlegen' : 'Änderungen speichern'}
                        </button>
                    </div>
                </div>
                </>
                ) : (
                    <div className="modal-projects-tab">
                        {loadingProjects ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Lade Projekte...</div>
                        ) : customerProjects.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Keine Projekte für diesen Kunden gefunden.</div>
                        ) : (
                            <div className="customer-projects-table-wrapper" style={{ marginTop: '15px' }}>
                                <SharedTable
                                    uniqueKeyField="id"
                                    data={customerProjects}
                                    onRowClick={(p) => {
                                        sessionStorage.setItem('customerModalBackup', JSON.stringify({
                                            isOpen: true,
                                            customerId: customer.id,
                                            currentMode: 'edit',
                                            activeTab: 'projects'
                                        }));
                                        onClose();
                                        navigate(`/verwaltung/projekte?id=${p.id}`);
                                    }}
                                    columns={[
                                        {
                                            key: 'project_number',
                                            label: 'Nummer',
                                            sortable: true
                                        },
                                        {
                                            key: 'name',
                                            label: 'Projektname',
                                            sortable: true
                                        },
                                        {
                                            key: 'address',
                                            label: 'Adresse',
                                            sortable: true,
                                            render: (p) => p.address || '—'
                                        },
                                        {
                                            key: 'status',
                                            label: 'Status',
                                            sortable: true,
                                            render: (p) => <StatusBadge status={p.status} />
                                        },
                                        {
                                            key: 'updated_at',
                                            label: 'Zuletzt geändert',
                                            sortable: true,
                                            render: (p) => p.updated_at ? formatDate(p.updated_at) : '—'
                                        }
                                    ]}
                                    emptyMessage="Keine Projekte für diesen Kunden gefunden."
                                />
                            </div>
                        )}
                        <div className="modal-footer" style={{ marginTop: '20px' }}>
                            <div className="footer-actions">
                                <button type="button" className="btn-secondary" onClick={onClose}>Schließen</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
